import React, { useEffect, useState, useCallback } from 'react';
import DataTable from 'react-data-table-component';
import { useNavigate } from 'react-router-dom';
import { getApi, deleteApi, postApi } from '../../utils/api/api';
import { showToast } from '../../utils/toast';
import { useLanguage } from '../../contexts/LanguageContext';
import Loader from '../../utils/loader';
import { showConfirmationAlert } from '../../utils/confirmationAlert';

const AdminEnquiryInfoList = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredData, setFilteredData] = useState([]);

  const handleViewClick = (id) => {
    navigate(`/buyer-dashboard/product-enquire/${id}`);
  };

  const fetchEnquiries = useCallback(async (page) => {
    setLoading(true);
    try {
      const response = await getApi(`admin/enquiry-list?page=${page}&per_page=${perPage}`);
      console.log("Admin Enquire Response::", response);
      
      if (response?.success) {
        const enquiryData = response.data?.enquiry || response.data?.enquiries || response.data?.data || [];
        setEnquiries(enquiryData);
        
        if (response.data?.total) {
          setTotalRows(response.data.total);
        } else if (response.data?.pagination?.total) {
          setTotalRows(response.data.pagination.total);
        } else {
          setTotalRows(enquiryData.length);
        }
      } else {
        setEnquiries([]);
        setTotalRows(0);
      }
    } catch (error) {
      console.error("Error fetching enquiries:", error);
      showToast('error', error.message || 'Failed to fetch enquiries.');
      setEnquiries([]);
      setTotalRows(0);
    } finally {
      setLoading(false);
    }
  }, [perPage]);

  useEffect(() => {
    fetchEnquiries(currentPage);
  }, [currentPage, fetchEnquiries]);

  useEffect(() => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const filtered = enquiries.filter(item => 
        (item.qty && item.qty.toString().includes(query)) ||
        (item.yarn_count && item.yarn_count.toString().includes(query)) ||
        (item.currency && item.currency.toLowerCase().includes(query)) ||
        (item.shippment && item.shippment.toLowerCase().includes(query)) ||
        (item.address && item.address.toLowerCase().includes(query))
      );
      setFilteredData(filtered);
    } else {
      setFilteredData(enquiries);
    }
  }, [searchQuery, enquiries]);

  const handleDelete = async (id) => {
    showConfirmationAlert({
      message: 'Are you sure you want to delete this enquiry?',
      onConfirm: async () => {
        try {
          const response = await deleteApi(`buyer/enquiry/${id}`);
          
          if (response.success) {
            showToast('success', 'Enquiry deleted successfully.');
            fetchEnquiries(currentPage);
          } else {
            showToast('error', response.message || 'Failed to delete enquiry.');
          }
        } catch (error) {
          showToast('error', error.message || 'Failed to delete enquiry.');
        }
      },
      onCancel: () => {
        console.log('Delete cancelled');
      }
    });
  };

  const handleStatusChange = async (id, status) => {
    const statusMessages = {
      'accepted': 'accept this enquiry',
      'not_accepted': 'mark this enquiry as not accepted',
      'delete': 'delete this enquiry'
    };
    let statusValue = status === "accepted" ? 2 : 3;
    console.log("statusValue", statusValue);
    showConfirmationAlert({
      message: `Are you sure you want to ${statusMessages[status]}?`,
      onConfirm: async () => {
        try {
          if (status === 'delete') {
            await handleDelete(id);
            return;
          }

          const response = await postApi(`buyer/enquiry-status/${id}/${statusValue}`, {
            status: status === 'accepted' ? 2 : 3
          });
          
          if (response.success) {
            showToast('success', `Enquiry ${status} successfully.`);
            fetchEnquiries(currentPage);
          } else {
            showToast('error', response.message || `Failed to ${status} enquiry.`);
          }
        } catch (error) {
          showToast('error', error.message || `Failed to ${status} enquiry.`);
        }
      },
      onCancel: () => {
        console.log('Status change cancelled');
      }
    });
  };

  const handleEdit = (enquiry) => {
    showToast('info', 'Edit functionality would be implemented here.');
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePerRowsChange = (newPerPage, page) => {
    setPerPage(newPerPage);
    setCurrentPage(1);
  };

  const columns = [
    {
      name: 'ID',
      selector: row => row.id,
      sortable: true,
      width: '80px'
    },
    {
      name: 'Quantity',
      selector: row => row.qty || 'N/A',
      sortable: true,
    },
    {
      name: 'Yarn Count',
      selector: row => row.yarn_count || 'N/A',
      sortable: true,
    },
    {
      name: 'Currency',
      selector: row => row.currency || 'N/A',
      sortable: true,
    },
    {
      name: 'Shipment',
      selector: row => row.shippment || 'N/A',
      sortable: true,
    },
    {
      name: 'Address',
      selector: row => row.address ? (row.address.length > 50 ? `${row.address.substring(0, 50)}...` : row.address) : 'N/A',
      sortable: true,
    },
    {
      name: 'Status',
      selector: row => {
        switch(row.status) {
          case 1: return 'Pending';
          case 2: return 'Approved';
          case 3: return 'Rejected';
          default: return 'Unknown';
        }
      },
      sortable: true,
    },
    // {
    //   name: 'Actions',
    //   cell: (row) => (
    //     <div className="dropdown">
    //       <button
    //         className="btn btn-sm btn-outline-secondary dropdown-toggle"
    //         type="button"
    //         id={`dropdownMenuButton-${row.id}`}
    //         data-bs-toggle="dropdown"
    //         aria-expanded="false"
    //       >
    //         <i className="fas fa-ellipsis-v"></i>
    //       </button>
    //       <ul className="dropdown-menu" aria-labelledby={`dropdownMenuButton-${row.id}`}>
    //         <li>
    //           <button
    //             className="dropdown-item text-success"
    //             onClick={() => handleStatusChange(row.id, 'accepted')}
    //             // disabled={row.status !== 1}
    //           >
    //             <i className="fas fa-check me-2"></i> Accepted
    //           </button>
    //         </li>
    //         <li>
    //           <button
    //             className="dropdown-item text-warning"
    //             onClick={() => handleStatusChange(row.id, 'not_accepted')}
    //             // disabled={row.status !== 1}
    //           >
    //             <i className="fas fa-times me-2"></i> Not Accepted
    //           </button>
    //         </li>
    //         <li>
    //           <button
    //             className="dropdown-item text-danger"
    //             onClick={() => handleStatusChange(row.id, 'delete')}
    //             disabled={row.status !== 1}
    //           >
    //             <i className="fas fa-trash-alt me-2"></i> Delete
    //           </button>
    //         </li>
    //       </ul>
    //     </div>
    //   ),
    //   ignoreRowClick: true,
    //   allowOverflow: true,
    //   button: true,
    // },
  ];

  const customStyles = {
    headRow: {
      style: {
        backgroundColor: '#f8f9fa',
        fontWeight: 'bold',
      },
    },
    rows: {
      style: {
        minHeight: '60px',
      },
    },
  };

      const totalPages = Math.ceil(totalRows / perPage);

  const renderStatusBadge = (status) => {
    const statusMap = {
      0: { label: 'Requested', color: 'primary' },
      1: { label: 'Pending', color: 'warning' },
      2: { label: 'Approved', color: 'success' },
      3: { label: 'Decline', color: 'danger' },
      4: { label: 'Invoice Submitted', color: 'info' },
    };
    const current = statusMap[status] || { label: 'Unknown', color: 'secondary' };

    return (
      <span className={`badge bg-${current.color} fs-6 shadow-sm`}>
        {current.label}
      </span>
    );
  };

  return (
   <div className="container py-4">
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-4">
        <h2 className="text-primary fw-bold mb-2">
          {t('enquiries.heading', 'Enquiry List')}
        </h2>
        <div className="col-12 col-md-4">
          <div className="input-group">
            <span className="input-group-text bg-white border-end-0">
              <i className="bi bi-search text-muted"></i>
            </span>
            <input
              type="text"
              className="form-control border-start-0 ps-0"
              placeholder="Search enquiries..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>
      </div>

      {loading ? (
        <Loader />
      ) : filteredData.length > 0 ? (
        <div className="row g-4">
          {filteredData.map((enquiry) => (
            <div key={enquiry.id} className="col-12">
              <div
                className="card border-0 shadow-lg flex-row flex-wrap align-items-stretch p-2"
                style={{
                  borderRadius: "12px",
                  transition: "0.3s",
                  overflow: "hidden",
                }}
              >
                {/* Image Section - Placeholder since no image in data yet */}
                <div
                  className="col-12 col-md-3 p-2 my-auto"
                  style={{ maxWidth: "350px" }}
                >
                  <div
                    className="d-flex align-items-center justify-content-center bg-light rounded"
                    style={{ minHeight: "180px", height: "100%" }}
                  >
                    <i className="bi bi-image fs-1 text-muted"></i>
                  </div>
                </div>

                <div className="col flex-grow-1 p-3 d-flex flex-column">
                  <div className="d-flex justify-content-between align-items-start mb-3">
                    <h6 className="card-subtitle text-muted mb-2">
                      ID: #{enquiry.id}
                    </h6>
                    {renderStatusBadge(enquiry.status)}
                  </div>


                  <div className="row g-2 mb-3">
                    <div className="col-6 col-md-4">
                      <small className="text-muted d-block">Quantity</small>
                      <strong>{enquiry.qty || 'N/A'}</strong>
                    </div>
                    <div className="col-6 col-md-4">
                      <small className="text-muted d-block">Yarn Count</small>
                      <strong>{enquiry.yarn_count || 'N/A'}</strong>
                    </div>
                    <div className="col-6 col-md-4">
                      <small className="text-muted d-block">Currency</small>
                      <strong>{enquiry.currency || 'N/A'}</strong>
                    </div>
                    <div className="col-6 col-md-4">
                      <small className="text-muted d-block">Address</small>
                      <span className="text-break">
                        {enquiry.address || 'N/A'}
                      </span>
                    </div>
                    <div className="col-6 col-md-4">
                      <small className="text-muted d-block">Shipment</small>
                      <span className="text-break">
                        {enquiry.shippment || 'N/A'}
                      </span>
                    </div>
                  </div>

                  <div className="mt-auto pt-3 border-top d-flex gap-2 justify-content-end">
                    {/* Actions: Accept / Not Accept - Always visible, disabled if not Pending (status 1) */}
              {/*      <button 
            className="btn btn-sm btn-outline-primary me-2"
            onClick={() => handleViewClick(enquiry.id)}
          >
            <i class="fas fa-pencil-alt"></i>
          </button>*/}
                   
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-5">
          <i className="bi bi-inbox display-4 text-muted mb-3"></i>
          <h4 className="text-muted">
            {t('enquiries.noEnquiries', 'No enquiries found.')}
          </h4>
        </div>
      )}

      {/* Pagination */}
      {!loading && totalPages > 1 && (
        <nav aria-label="Enquiries pagination" className="mt-5">
          <ul className="pagination justify-content-center">
            {/* Previous Button */}
            <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
              <button
                className="page-link"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                <i className="bi bi-chevron-left"></i>
              </button>
            </li>

            {/* Page Numbers */}
            {[...Array(totalPages)].map((_, i) => {
              return (
                <li
                  key={i}
                  className={`page-item ${currentPage === i + 1 ? "active" : ""}`}
                  onClick={() => handlePageChange(i + 1)}
                >
                  <button className="page-link">{i + 1}</button>
                </li>
              );
            })}

            {/* Next Button */}
            <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
              <button
                className="page-link"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                <i className="bi bi-chevron-right"></i>
              </button>
            </li>
          </ul>
        </nav>
      )}
    </div>
  );
};

export default AdminEnquiryInfoList;